"use client"

import * as React from "react"
import { DeleteIcon } from "lucide-react"
import { currencyScale } from "@ibada/shared-types/finance"

import { cn, Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@ibada/ui"

import { getCurrencyPrefix } from "@/features/finance/lib/finance-utils"
import { useLocale } from "@/i18n"

/** The grouping and decimal characters the active locale uses (fr: narrow space and comma; en: comma and dot). */
function localeSeparators(locale: string) {
  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1)
  return {
    group: parts.find((part) => part.type === "group")?.value ?? ",",
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
  }
}

/**
 * Formats the canonical dot-decimal money string for display WITHOUT converting it to a number, so what is shown
 * is exactly what was typed: "1250.5" → "1 250,5" (fr). Pass `padDecimals` to show the currency's full scale.
 */
export function formatAmountText(value: string, locale: string, scale: number, padDecimals = false): string {
  const { group, decimal } = localeSeparators(locale)
  const [rawInt = "", rawDec] = (value || "0").split(".")
  const int = (rawInt.replace(/^0+(?=\d)/, "") || "0").replace(/\B(?=(\d{3})+(?!\d))/g, group)
  if (scale === 0) return int
  if (rawDec === undefined) return padDecimals ? `${int}${decimal}${"0".repeat(scale)}` : int
  return `${int}${decimal}${padDecimals ? rawDec.padEnd(scale, "0") : rawDec}`
}

/** Applies one number-pad key to the canonical string, refusing what money cannot hold. */
export function applyAmountKey(current: string, key: string, scale: number): string {
  if (key === "back") return current.slice(0, -1)
  if (key === "decimal") {
    if (scale === 0 || current.includes(".")) return current
    return `${current || "0"}.`
  }
  if (!/^\d$/.test(key)) return current
  const [int = "", dec] = current.split(".")
  if (dec !== undefined) return dec.length >= scale ? current : `${current}${key}`
  if (int.length >= 12) return current
  return int === "0" ? key : `${current}${key}`
}

/**
 * Turns what was typed into the canonical dot-decimal money string: a comma or a dot is the decimal mark, anything
 * else that is not a digit is dropped, and decimals stop at the currency's scale. "1 250,5" → "1250.5".
 */
export function sanitizeAmountInput(text: string, scale: number): string {
  const cleaned = text.replace(/,/g, ".").replace(/[^\d.]/g, "")
  const [int = "", ...rest] = cleaned.split(".")
  if (rest.length === 0 || scale === 0) return int
  return `${int || "0"}.${rest.join("").slice(0, scale)}`
}

/**
 * An amount typed directly in a row (a fund split line): the phone's decimal keypad, the currency after it, and
 * the canonical string handed back. For the one amount a form is about, use `MobileAmountField` instead.
 */
export function MobileAmountInput({
  value,
  onChange,
  currencyCode,
  placeholder = "0",
  ariaLabel,
  disabled = false,
  className,
}: {
  value: string
  onChange: (value: string) => void
  currencyCode: string
  placeholder?: string
  ariaLabel: string
  disabled?: boolean
  className?: string
}) {
  const locale = useLocale()
  const scale = currencyScale(currencyCode)
  const { decimal } = localeSeparators(locale)
  return (
    <span className={cn("inline-flex items-baseline gap-1", className)}>
      <input
        inputMode={scale === 0 ? "numeric" : "decimal"}
        enterKeyHint='done'
        aria-label={ariaLabel}
        disabled={disabled}
        value={value.replace(".", decimal)}
        placeholder={placeholder}
        onChange={(event) => onChange(sanitizeAmountInput(event.target.value, scale))}
        className='w-24 bg-transparent text-right text-base font-extrabold tabular-nums text-foreground outline-none placeholder:font-semibold placeholder:text-muted-foreground/60'
      />
      <span className='text-base font-bold text-muted-foreground'>{getCurrencyPrefix(currencyCode)}</span>
    </span>
  )
}

/**
 * One formatter for money shown in phone forms ("1 250,00 $"), the same as the amount at the top of the form, so a
 * balance or a split line never reads "$1,250.00" beside it.
 */
export function useFormatMoneyText(currencyCode: string) {
  const locale = useLocale()
  const scale = currencyScale(currencyCode)
  const symbol = getCurrencyPrefix(currencyCode)
  return React.useCallback(
    (value: string | null | undefined) => `${formatAmountText(value || "", locale, scale, true)} ${symbol}`,
    [locale, scale, symbol],
  )
}

/**
 * The display size for an amount: large while it fits a phone, stepping down as the figure grows, so a big total
 * ("1 250 000 000,00 $") shrinks instead of running off the screen.
 */
export function amountDisplaySizeClass(text: string, base: "field" | "pad"): string {
  const length = text.length
  if (base === "pad") return length > 16 ? "text-3xl" : length > 12 ? "text-4xl" : "text-5xl"
  return length > 16 ? "text-[1.875rem]" : length > 12 ? "text-[2.25rem]" : "text-[2.75rem]"
}

/** Which way the money goes: money out (an expense) shows the amount in red. */
export enum MobileAmountTone {
  Neutral = "neutral",
  Out = "out",
}

type MobileAmountFieldProps = {
  /** The canonical dot-decimal money string ("" while nothing is typed). */
  value: string
  onChange: (value: string) => void
  currencyCode: string
  label: string
  /** Shown under the amount, e.g. "Touchez pour saisir". */
  hint?: string
  /** The number pad's confirm label. */
  doneLabel: string
  invalid?: boolean
  disabled?: boolean
  tone?: MobileAmountTone
}

/**
 * The amount as the large figure at the top of a money form. Tapping it never opens the phone keyboard: it slides
 * up the app's own number pad in a bottom sheet, which formats as it is typed. Tap it again to change it.
 */
export function MobileAmountField({
  value,
  onChange,
  currencyCode,
  label,
  hint,
  doneLabel,
  invalid = false,
  disabled = false,
  tone = MobileAmountTone.Neutral,
}: MobileAmountFieldProps) {
  const locale = useLocale()
  const scale = currencyScale(currencyCode)
  const [open, setOpen] = React.useState(false)
  const symbol = getCurrencyPrefix(currencyCode)

  return (
    <>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen(true)}
        className='flex w-full flex-col items-center gap-1 py-3 text-center active:opacity-70 disabled:opacity-60'
      >
        <span className={cn("text-[13px] font-bold", invalid ? "text-destructive" : "text-muted-foreground")}>{label}</span>
        <span
          className={cn(
            "max-w-full break-all leading-none font-extrabold tracking-tight tabular-nums",
            amountDisplaySizeClass(formatAmountText(value, locale, scale, true), "field"),
            !value ? "text-muted-foreground/50" : tone === MobileAmountTone.Out ? "text-destructive" : "text-foreground",
          )}
        >
          {formatAmountText(value, locale, scale, true)}{" "}
          <span className={value && tone === MobileAmountTone.Out ? "text-destructive/70" : "text-muted-foreground"}>{symbol}</span>
        </span>
        {hint ? <span className='text-[13px] font-semibold text-muted-foreground/70'>{hint}</span> : null}
      </button>
      <MobileNumberPadSheet
        open={open}
        onOpenChange={setOpen}
        value={value}
        onConfirm={onChange}
        currencyCode={currencyCode}
        title={label}
        doneLabel={doneLabel}
      />
    </>
  )
}

type MobileNumberPadSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: string
  onConfirm: (value: string) => void
  currencyCode: string
  title: string
  doneLabel: string
}

/** The number pad: plain digits on the sheet (no key backgrounds), the amount formatted above as it is typed. */
export function MobileNumberPadSheet({ open, onOpenChange, value, onConfirm, currencyCode, title, doneLabel }: MobileNumberPadSheetProps) {
  const locale = useLocale()
  const scale = currencyScale(currencyCode)
  const { decimal } = localeSeparators(locale)
  const symbol = getCurrencyPrefix(currencyCode)
  const [draft, setDraft] = React.useState(value)

  React.useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const press = (key: string) => setDraft((current) => applyAmountKey(current, key, scale))
  const confirm = () => {
    // A trailing separator ("12.") is not an amount yet; drop it rather than store half a number.
    onConfirm(draft.endsWith(".") ? draft.slice(0, -1) : draft)
    onOpenChange(false)
  }

  const keys: { id: string; label: React.ReactNode; aria?: string }[] = [
    ..."123456789".split("").map((digit) => ({ id: digit, label: digit })),
    { id: "decimal", label: scale === 0 ? "" : decimal, aria: decimal },
    { id: "0", label: "0" },
    { id: "back", label: <DeleteIcon className='size-6' />, aria: "⌫" },
  ]

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className='flex-row items-center justify-between group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
          <DrawerTitle className='min-w-0 flex-1 truncate text-[15px] font-bold text-muted-foreground'>{title}</DrawerTitle>
          <DrawerDescription className='sr-only'>{title}</DrawerDescription>
          <button type='button' onClick={confirm} className='rounded-full px-2 py-1 text-base font-extrabold text-primary active:bg-primary/10'>
            {doneLabel}
          </button>
        </DrawerHeader>
        <div className='px-4 pb-4 text-center'>
          <span
            className={cn(
              "break-all font-extrabold tracking-tight tabular-nums",
              amountDisplaySizeClass(formatAmountText(draft, locale, scale), "pad"),
              draft ? "text-foreground" : "text-muted-foreground/50",
            )}
          >
            {formatAmountText(draft, locale, scale)}
          </span>{" "}
          <span className='text-4xl font-bold text-muted-foreground'>{symbol}</span>
        </div>
        <div className='grid grid-cols-3 gap-1 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
          {keys.map((key) => (
            <button
              key={key.id}
              type='button'
              aria-label={key.aria}
              disabled={key.id === "decimal" && scale === 0}
              onClick={() => press(key.id)}
              className='flex h-14 items-center justify-center rounded-2xl text-[1.75rem] font-semibold text-foreground transition-colors active:bg-muted'
            >
              {key.label}
            </button>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
